import AddIcon from '@mui/icons-material/Add'
import PeopleIcon from '@mui/icons-material/People'
import PersonIcon from '@mui/icons-material/Person'
import {
  Box,
  Button,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface TemplateEditorFooterProps {
  canEdit: boolean
  canDelete: boolean
  canChangeOwnerScope: boolean
  isDeleting: boolean
  isDirty: boolean
  isSaving?: boolean
  selectedId: string | 'new'
  onSave: () => void
  onDelete: () => void
  hasPractices: boolean
  ownerScope: 'farmer' | 'all'
  draftName: string
  onOwnerScopeChange: (scope: 'farmer' | 'all') => void
  availableTraitIds: string[]
  onAddPractice: (traitId: string) => void
}

export const TemplateEditorFooter = ({
  canEdit,
  canDelete,
  canChangeOwnerScope,
  isDeleting,
  isDirty,
  isSaving,
  selectedId,
  onSave,
  onDelete,
  draftName,
  ownerScope,
  onOwnerScopeChange,
  availableTraitIds,
  hasPractices,
  onAddPractice,
}: TemplateEditorFooterProps) => {
  const { t } = useTranslation(['traits', '@cibo/programs/TemplateEditorModal'])
  const [addPracticeAnchorEl, setAddPracticeAnchorEl] = useState<HTMLElement | null>(null)

  if (!canEdit && !canDelete) return null

  return (
    <>
      <Divider />
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5 }}>
        {canEdit && (
          <>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              size="small"
              data-testid="template-add-practice-button"
              disabled={availableTraitIds.length === 0}
              onClick={(e: MouseEvent<HTMLButtonElement>) =>
                setAddPracticeAnchorEl(e.currentTarget)
              }
            >
              {t('addPractice', { ns: '@cibo/programs/TemplateEditorModal' })}
            </Button>
            {canChangeOwnerScope && (
              <Select
                size="small"
                data-testid="template-owner-scope-select"
                value={ownerScope}
                onChange={e => onOwnerScopeChange(e.target.value as 'farmer' | 'all')}
                renderValue={value => (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    {value === 'farmer' ? (
                      <PersonIcon fontSize="small" />
                    ) : (
                      <PeopleIcon fontSize="small" />
                    )}
                    <Typography variant="body2">
                      {t(value === 'farmer' ? 'onlyThisFarmer' : 'allFarmers', {
                        ns: '@cibo/programs/TemplateEditorModal',
                      })}
                    </Typography>
                  </Stack>
                )}
              >
                <MenuItem value="farmer">
                  <ListItemIcon>
                    <PersonIcon fontSize="small" />
                  </ListItemIcon>
                  {t('onlyThisFarmer', { ns: '@cibo/programs/TemplateEditorModal' })}
                </MenuItem>
                <MenuItem value="all">
                  <ListItemIcon>
                    <PeopleIcon fontSize="small" />
                  </ListItemIcon>
                  {t('allFarmers', { ns: '@cibo/programs/TemplateEditorModal' })}
                </MenuItem>
              </Select>
            )}
          </>
        )}

        <Box sx={{ flex: 1 }} />

        <Button
          variant="contained"
          data-testid="template-save-button"
          loading={isSaving}
          disabled={!isDirty || isSaving || !hasPractices || !draftName.trim()}
          onClick={onSave}
        >
          {t('saveTemplate', { ns: '@cibo/programs/TemplateEditorModal' })}
        </Button>

        <Button
          variant="outlined"
          color="error"
          data-testid="template-delete-button"
          disabled={!canDelete || selectedId === 'new'}
          loading={isDeleting}
          onClick={onDelete}
        >
          {t('deleteTemplate', { ns: '@cibo/programs/TemplateEditorModal' })}
        </Button>
      </Stack>

      <Menu
        anchorEl={addPracticeAnchorEl}
        open={!!addPracticeAnchorEl}
        onClose={() => setAddPracticeAnchorEl(null)}
      >
        {availableTraitIds.length === 0 ? (
          <MenuItem disabled>
            {t('allPracticesAdded', { ns: '@cibo/programs/TemplateEditorModal' })}
          </MenuItem>
        ) : (
          availableTraitIds.map(traitId => (
            <MenuItem
              key={traitId}
              onClick={() => {
                onAddPractice(traitId)
                setAddPracticeAnchorEl(null)
              }}
            >
              {t(traitId, { ns: 'traits' })}
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  )
}
